import { transactionActionExecutionProfiles } from './executionProfiles';
import type { CleanupTransactionProposal } from './features/cleanup-transaction';
import { cleanupTransactionActionCapability } from './features/cleanup-transaction';
import type { RecategorizeProposal } from './features/recategorize';
import { recategorizeTransactionActionCapability } from './features/recategorize';
import type { RenamePayeeProposal } from './features/rename-payee';
import { renamePayeeTransactionActionCapability } from './features/rename-payee';
import type {
  TransactionActionCapability,
  TransactionActionEligibleTransaction,
  TransactionActionExecutionProfile,
  TransactionActionRunnerId,
} from './proposalTypes';

// The capability registry described in docs/features/transaction-actions/README.md ("Capability
// registry"): the single place that maps a TransactionActionCapability to its runner, execution
// profile, and (for structured-model capabilities) response parser. Adding a capability means
// adding an entry here, not branching framework logic elsewhere.
export type TransactionActionCapabilityDefinition = {
  capability: TransactionActionCapability;
  supportedInPhase1: boolean;
  executionProfile: TransactionActionExecutionProfile;
  runnerId: TransactionActionRunnerId;
  parseStructuredResponse?: (
    eligibleTransactions: TransactionActionEligibleTransaction[],
    assistantResponse: string,
  ) => RecategorizeProposal[] | RenamePayeeProposal[] | CleanupTransactionProposal[] | null;
};

export const transactionActionCapabilities: Record<TransactionActionCapability, TransactionActionCapabilityDefinition> =
  {
    recategorize: recategorizeTransactionActionCapability,
    rename_payee: renamePayeeTransactionActionCapability,
    cleanup_transaction: cleanupTransactionActionCapability,
    create_rule: {
      capability: 'create_rule',
      supportedInPhase1: false,
      executionProfile: transactionActionExecutionProfiles.create_rule,
      runnerId: 'structured_model',
    },
  };

export const getTransactionActionCapabilityDefinition = (
  capability: TransactionActionCapability,
): TransactionActionCapabilityDefinition => transactionActionCapabilities[capability];

export const isTransactionActionCapabilitySupportedInPhase1 = (capability: TransactionActionCapability): boolean =>
  transactionActionCapabilities[capability].supportedInPhase1;
